// Form 9 - Alloted Assessors For Assessment
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Tooltip,
  Button,
  ButtonGroup,
  alpha,
} from "@mui/material";

import ArticleIcon from "@mui/icons-material/Article";
import GavelIcon from "@mui/icons-material/Gavel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EventIcon from "@mui/icons-material/Event";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import FeedbackIcon from "@mui/icons-material/Feedback";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";

import { decryptAppId, decryptText, encryptAppId, encryptText } from "../../utils/encryption";
import PageTitle from "../../components/PageTitle";
import axios from "axios";
import s3Utils from "../../utils/s3Utils";

const NCApplicationFinding = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [rows, setRows] = useState([]); // ✅ always array
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState("finding"); // finding, feedback, evidence, equipment
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");

  // Define colors
  const colors = {
    primary: { main: "#1976d2" },
    secondary: { main: "#9c27b0" },
    error: { main: "#d32f2f" },
    success: { main: "#2e7d32" },
    grey: { 100: "#f5f5f5", 300: "#e0e0e0" },
  };

  // 🔐 Encrypted params
  const encryptedAppId = searchParams.get("appId");
  const encryptedAssessmentId = searchParams.get("assessmentId");
  const encryptedOrgId = searchParams.get("orgId");

  // 🔓 Decrypt
  const appId = encryptedAppId ? decryptAppId(encryptedAppId) : null;
  const assessmentId = encryptedAssessmentId
    ? decryptText(encryptedAssessmentId)
    : null;
  const orgId = encryptedOrgId ? decryptText(encryptedOrgId) : null;

  // 🔄 Fetch Application Finding (Mode 3 - NC Statements)
  const fetchApplicationFinding = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessment-report/application-finding`,
        {
          params: {
            appId,
            assessmentId,
          },
        }
      );

      console.log("Application Finding Response:", response.data);
      setRows(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching application finding:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // 🔄 Fetch Observation Report (Mode 1 - Assessor Observations)
  const fetchObservationReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessment-report/application-finding-feedback`,
        {
          params: {
            appId,
            assessmentId,
          },
        }
      );

      console.log("Observation Report Response:", response.data);
      setRows(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching observation report:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // 🔄 Fetch Evidence Report (Mode 2 - Assessor Evidence)
  const fetchEvidenceReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessment-report/application-finding-evidence`,
        {
          params: {
            appId,
            assessmentId,
          },
        }
      );

      console.log("Evidence Report Response:", response.data);
      setRows(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching evidence report:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // 🔄 Fetch Verify Equipment (Mode 4)
  const fetchVerifyEquipment = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessment-report/verify-equipment`,
        {
          params: {
            appId,
            assessmentId,
          },
        }
      );

      console.log("Verify Equipment Response:", response.data);
      setRows(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching verify equipment:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle button click
  const handleViewChange = (view) => {
    setActiveView(view);
    if (view === "finding") {
      fetchApplicationFinding();
    } else if (view === "feedback") {
      fetchObservationReport();
    } else if (view === "evidence") {
      fetchEvidenceReport();
    } else if (view === "equipment") {
      fetchVerifyEquipment();
    }
  };

  // Lightbox handlers
  const handleOpenLightbox = (imageUrl) => {
    setLightboxImage(imageUrl);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage("");
  };

  useEffect(() => {
    if (appId && assessmentId) {
      fetchApplicationFinding(); // Load application finding by default
    } else {
      setLoading(false);
    }
  }, [appId, assessmentId]);

  return (
    <>
      <PageTitle title="NABL | Assessment Reports" />

      <Box sx={{ mt: 10, px: 5 }}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <AssignmentTurnedInIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Assessment Reports
            </Typography>
          </Box>

          <Box display="flex" gap={2}>
            {/* Button Group */}
            <ButtonGroup variant="contained" size="medium">
              <Button
                onClick={() => handleViewChange("finding")}
                startIcon={<FindInPageIcon />}
                variant={activeView === "finding" ? "contained" : "outlined"}
                color="primary"
              >
                Application Finding
              </Button>
              <Button
                onClick={() => handleViewChange("feedback")}
                startIcon={<FeedbackIcon />}
                variant={activeView === "feedback" ? "contained" : "outlined"}
                color="secondary"
              >
                Checklist Observation Report
              </Button>
              <Button
                onClick={() => handleViewChange("evidence")}
                startIcon={<ImageIcon />}
                variant={activeView === "evidence" ? "contained" : "outlined"}
                color="success"
              >
                Evidence Report
              </Button>
              <Button
                onClick={() => handleViewChange("equipment")}
                startIcon={<VisibilityIcon />}
                variant={activeView === "equipment" ? "contained" : "outlined"}
                color="warning"
              >
              Equipment Report
              </Button>
            </ButtonGroup>

            {/* Application Preview Report Button */}
            <Button
              variant="contained"
              color="info"
              size="medium"
              startIcon={<ArticleIcon />}
              onClick={() => {
                const url = `/main/assessment-report?appId=${encryptAppId(
                  appId
                )}&assessmentId=${encryptText(
                  assessmentId
                )}&orgId=${encryptText(
                  orgId
                )}&observation=${encryptText(
                  'true'
                )}`;
                window.open(url, "_blank", "noopener,noreferrer");
              }}
            >
              Applicaiton Preview Report
            </Button>
          </Box>
        </Box>

        <Paper elevation={3}>
          {loading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={200}
            >
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box p={4} textAlign="center">
              <Typography color="text.secondary">
                No records found
              </Typography>
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width="5%">#</TableCell>

                    {activeView === "finding" && (
                      <>
                        <TableCell width="20%">Inserted By Name</TableCell>
                        <TableCell width="20%">Section Name</TableCell>
                        <TableCell width="10%">Clause No</TableCell>
                        <TableCell width="35%">Observation</TableCell>
                        <TableCell width="15%">Insert Date</TableCell>
                      </>
                    )}

                    {activeView === "feedback" && (
                      <>
                        <TableCell width="20%">Assessor Name</TableCell>
                        <TableCell width="10%">Clause No</TableCell>
                        <TableCell width="50%">Remarks</TableCell>
                        <TableCell width="20%">Insert Date</TableCell>
                      </>
                    )}

                    {activeView === "evidence" && (
                      <>
                        <TableCell width="30%">Inserted By Name</TableCell>
                        <TableCell width="15%">Clause No</TableCell>
                        <TableCell width="55%">Evidence</TableCell>
                      </>
                    )}

                    {activeView === "equipment" && (
                      <>
                        <TableCell width="6%">Equipment ID</TableCell>
                        <TableCell width="10%">Equipment Name</TableCell>
                        <TableCell width="6%">UID</TableCell>
                        <TableCell width="8%">Make/Model</TableCell>
                        <TableCell width="6%">Year</TableCell>
                        <TableCell width="8%">Receipt Date</TableCell>
                        <TableCell width="6%">Range</TableCell>
                        <TableCell width="6%">Accuracy</TableCell>
                        <TableCell width="8%">Last Calibration</TableCell>
                        <TableCell width="8%">Calibration Due</TableCell>
                        <TableCell width="7%">Working Status</TableCell>
                        <TableCell width="6%">Verified</TableCell>
                        <TableCell width="10%">Verify Remarks</TableCell>
                        <TableCell width="8%">Verify Date</TableCell>
                        <TableCell width="6%">NC Clause</TableCell>
                        <TableCell width="11%">NC Statement</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow hover key={row.id || index}>
                      <TableCell>{index + 1}</TableCell>

                      {activeView === "finding" && (
                        <>
                        <TableCell>
                            <Typography fontWeight={500}>
                              {row["inserted by Name"] || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight={500}>
                              {row.SectionName || "-"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            {row.clauseNo ? (
                              <Chip
                                label={row.clauseNo}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ) : (
                              <Chip label="N/A" size="small" variant="outlined" />
                            )}
                          </TableCell>

                          <TableCell>
                            <Tooltip title={row.Observation || ""}>
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: 400,
                                }}
                              >
                                {row.Observation || "-"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {row.Insertdate
                                ? new Date(row.Insertdate).toLocaleString("en-IN", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit"
                                })
                                : "-"}
                            </Typography>
                          </TableCell>
                        </>
                      )}

                      {activeView === "feedback" && (
                        <>
                          <TableCell>
                            <Typography fontWeight={500}>
                              {row["Assessor Name"] || "-"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            {row.ClauseNo ? (
                              <Chip
                                label={row.ClauseNo}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            ) : (
                              <Chip label="N/A" size="small" variant="outlined" />
                            )}
                          </TableCell>

                          <TableCell>
                            <Tooltip title={row.AnsRemarks || ""}>
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: 400,
                                }}
                              >
                                {row.AnsRemarks || "-"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {row.InsertDate
                                ? new Date(row.InsertDate).toLocaleString("en-IN", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit"
                                })
                                : "-"}
                            </Typography>
                          </TableCell>
                        </>
                      )}

                      {activeView === "evidence" && (
                        <>
                          <TableCell>
                            <Typography fontWeight={500}>
                              {row["Inserted By Name"] || row.CabName || "-"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            {row.ClauseNo ? (
                              <Chip
                                label={row.ClauseNo}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            ) : (
                              <Chip label="N/A" size="small" variant="outlined" />
                            )}
                          </TableCell>

                          <TableCell>
                            {row.Evidence ? (
                              <WorkingSpaceDocument
                                doc={row.Evidence}
                                index={index}
                                isImage={row.Evidence.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/)}
                                isPdf={row.Evidence.toLowerCase().endsWith(".pdf")}
                                isExcel={row.Evidence.toLowerCase().match(/\.(xlsx|xls)$/)}
                                colors={colors}
                                onImageClick={handleOpenLightbox}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </TableCell>
                        </>
                      )}

                      {activeView === "equipment" && (
                        <>
                          <TableCell>
                            <Chip
                              label={row.Equipmentid || "-"}
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                          </TableCell>

                          <TableCell>
                            <Typography fontWeight={500}>
                              {row.NameOfEquipment || "-"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {row.Uid || "-"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {row.MakeCompany && row.Model 
                                ? `${row.MakeCompany} / ${row.Model}`
                                : row.MakeCompany || row.Model || "-"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {row.YearOfMake || "-"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {row.ReceiptDate
                                ? new Date(row.ReceiptDate).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric"
                                })
                                : "-"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {row.Range || "-"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {row.Accuracy || "-"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {row.DateOfLastCalibration
                                ? new Date(row.DateOfLastCalibration).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric"
                                })
                                : "-"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {row.CalibrationDueOn
                                ? new Date(row.CalibrationDueOn).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric"
                                })
                                : "-"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={row.Workingstatus || "Unknown"}
                              size="small"
                              color={row.Workingstatus === "Working" ? "success" : "default"}
                              variant="outlined"
                            />
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={row.isverified || "Pending"}
                              size="small"
                              color={row.isverified === "Yes" ? "success" : "error"}
                              variant="filled"
                            />
                          </TableCell>

                          <TableCell>
                            <Tooltip title={row.VerifyRemarks || ""}>
                              <Typography variant="body2" sx={{ maxWidth: 200 }}>
                                {row.VerifyRemarks || "-"}
                              </Typography>
                            </Tooltip>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {row.Verifydate
                                ? new Date(row.Verifydate).toLocaleDateString("en-IN", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric"
                                })
                                : "-"}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            {row.nc_Clause ? (
                              <Chip
                                label={row.nc_Clause}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            ) : (
                              <Chip label="N/A" size="small" variant="outlined" />
                            )}
                          </TableCell>

                          <TableCell>
                            <Tooltip title={row.nc_statement || ""}>
                              <Typography
                                variant="body2"
                                sx={{
                                  maxWidth: 250,
                                }}
                              >
                                {row.nc_statement || "-"}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Lightbox for image preview */}
      {lightboxOpen && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "pointer",
          }}
          onClick={handleCloseLightbox}
        >
          <Box sx={{ maxWidth: "90%", maxHeight: "90%" }}>
            <img
              src={lightboxImage}
              alt="Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
              }}
            />
          </Box>
          <Button
            variant="contained"
            sx={{
              position: "absolute",
              top: 20,
              right: 20,
              bgcolor: "white",
              color: "black",
              "&:hover": { bgcolor: "grey.300" },
            }}
            onClick={handleCloseLightbox}
          >
            Close
          </Button>
        </Box>
      )}
    </>
  );
};

// Working Space Document Component to handle images and PDF/Excel thumbnails
const WorkingSpaceDocument = ({
  doc,
  index,
  isPdf,
  isExcel,
  colors,
  onImageClick,
}) => {
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadDocument = async () => {
      try {
        setLoading(true);
        if (doc) {
          // Check if it's just a filename or full URL
          if (s3Utils.isJustFilename(doc)) {
            const signedUrl = await s3Utils.generateSignedUrlForFilename(doc);
            setImageUrl(signedUrl);
          } else {
            setImageUrl(doc);
          }
        }
      } catch (error) {
        console.error("Error loading document:", error);
        setImageUrl("");
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [doc]);

  if (loading) {
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: colors.grey[100],
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // For PDF/Excel files, show thumbnail with icon
  if (isPdf || isExcel) {
    return (
      <Box
        sx={{
          width: 80,
          height: 80,
          border: `2px solid ${colors.grey[300]}`,
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          transition: "all 0.2s",
          bgcolor: isPdf
            ? alpha(colors.error.main, 0.05)
            : alpha(colors.success.main, 0.05),
          "&:hover": {
            borderColor: isPdf ? colors.error.main : colors.success.main,
            transform: "scale(1.05)",
            boxShadow: 3,
          },
        }}
        onClick={() => window.open(imageUrl, "_blank")}
      >
        {isPdf ? (
          <PictureAsPdfIcon
            sx={{ fontSize: 30, color: colors.error.main, mb: 1 }}
          />
        ) : (
          <DescriptionIcon
            sx={{ fontSize: 30, color: colors.success.main, mb: 1 }}
          />
        )}
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, textAlign: "center", px: 1 }}
        >
          {isPdf ? "PDF" : "Excel"}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: "0.40rem" }}
        >
          Click to view
        </Typography>
      </Box>
    );
  }

  // For images, show the actual image
  return (
    <Box
      sx={{
        width: 80,
        height: 80,
        border: `2px solid ${colors.grey[300]}`,
        borderRadius: 2,
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: colors.primary.main,
          transform: "scale(1.05)",
          boxShadow: 3,
        },
      }}
      onClick={() =>
        onImageClick ? onImageClick(imageUrl) : window.open(imageUrl, "_blank")
      }
    >
      <img
        src={imageUrl}
        alt={`Evidence ${index + 1}`}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
        onError={(e) => {
          e.target.src =
            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Crect fill='%23f0f0f0' width='120' height='120'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23999' font-size='12'%3EImage%3C/text%3E%3C/svg%3E";
        }}
      />
    </Box>
  );
};

export default NCApplicationFinding;
